import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Form, Spinner, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import useUtenti from '../../hooks/useUtenti';
import AgenzieService from '../../services/AgenzieService/AgenzieService';
import CustomPagination from '../../components/CustomPagination/CustomPagination';
import SearchableSelect from '../../components/SearchableSelect/SearchableSelect';
import SearchUtenteCorsista from '../../components/SearchUtenteCorsista/SearchUtenteCorsista';
import UtilityService from '../../services/UtillityServices/UtilityService';
import { ROLES } from '../../model/roles';

const TIPO_EVENTO_LABELS = {
  creazione_invito: 'Creazione invito',
  modifica_anagrafica: 'Modifica anagrafica',
  eliminazione_invito: 'Eliminazione invito',
};

const RUOLO_LABELS = {
  admin: 'Admin',
  developer: 'Developer',
  course_designer: 'Course designer',
  partner: 'Partner',
  superpartner: 'Superpartner',
};

const AUTORE_RUOLI = [
  { value: '', label: 'Qualsiasi autore' },
  { value: 'admin', label: 'Admin' },
  { value: 'developer', label: 'Developer' },
  { value: 'course_designer', label: 'Course designer' },
  { value: 'partner', label: 'Partner' },
  { value: 'superpartner', label: 'Superpartner' },
];

const LogInvitiUtentiPage = () => {
  const userLoggedIn = useSelector((state) => state?.user?.userData);
  const isPartner = userLoggedIn?.role === ROLES.PARTNER;
  const partnerAgenziaId = isPartner ? String(userLoggedIn?.id_agenzia ?? '') : '';

  const { findLogInvitiUtenti, loadingStateUtenti } = useUtenti();

  const [agenzie, setAgenzie] = useState([]);
  const [agenzieLoading, setAgenzieLoading] = useState(false);
  const [selectedAgenzia, setSelectedAgenzia] = useState(null);
  const [selectedUtente, setSelectedUtente] = useState(null);
  const [logs, setLogs] = useState([]);
  const [selectedPage, setSelectedPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasSearched, setHasSearched] = useState(false);
  const filtersRef = useRef({});
  const [formDataFilter, setFormDataFilter] = useState({
    id_agenzia: partnerAgenziaId,
    autore: '',
    ruolo_autore: '',
    tipo_evento: '',
    solo_con_voucher: false,
    data_da: '',
    data_a: '',
  });

  useEffect(() => {
    let cancelled = false;
    const loadAgenzie = async () => {
      setAgenzieLoading(true);
      try {
        const response = await AgenzieService.getAllAgenzie();
        if (!response?.ok || cancelled) return;
        const list = await response.json();
        const sorted = (list ?? []).slice().sort((a, b) =>
          (a.denominazione ?? '').localeCompare(b.denominazione ?? '', 'it', { sensitivity: 'base' })
        );
        if (!cancelled) {
          setAgenzie(sorted);
          if (isPartner && partnerAgenziaId) {
            const mine = sorted.find((a) => String(a.id_agenzia) === partnerAgenziaId);
            if (mine) setSelectedAgenzia(mine);
          }
        }
      } catch {
        if (!cancelled) setAgenzie([]);
      } finally {
        if (!cancelled) setAgenzieLoading(false);
      }
    };
    loadAgenzie();
    return () => { cancelled = true; };
  }, [isPartner, partnerAgenziaId]);

  const buildFiltri = useCallback(() => {
    const idAgenzia = selectedAgenzia?.id_agenzia ?? (formDataFilter.id_agenzia || null);
    const filtri = {};
    if (idAgenzia) filtri.id_agenzia = String(idAgenzia);
    if (selectedUtente?.id_utente) filtri.id_utente = selectedUtente.id_utente;
    if (formDataFilter.ruolo_autore) {
      filtri.autore = formDataFilter.ruolo_autore;
    } else if (formDataFilter.autore?.trim()) {
      filtri.autore = formDataFilter.autore.trim();
    }
    if (formDataFilter.tipo_evento) filtri.tipo_evento = formDataFilter.tipo_evento;
    if (formDataFilter.solo_con_voucher) filtri.solo_con_voucher = 1;
    if (formDataFilter.data_da) filtri.data_da = formDataFilter.data_da;
    if (formDataFilter.data_a) filtri.data_a = formDataFilter.data_a;
    return filtri;
  }, [selectedAgenzia, selectedUtente, formDataFilter]);

  const fetchLogs = useCallback(async (page, filtriOverride = null) => {
    const filtri = filtriOverride ?? filtersRef.current;
    const response = await findLogInvitiUtenti({
      filtri,
      pagina: page,
      limite: 15,
      ordinamento: 'rowversion DESC',
    });
    if (response) {
      setLogs(response.data ?? []);
      setTotalPages(Math.max(1, Math.ceil((response.count ?? 0) / 15)));
    }
  }, [findLogInvitiUtenti]);

  const handlePageChange = (page) => {
    setSelectedPage(page);
    if (hasSearched) {
      fetchLogs(page);
    }
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    const filtri = buildFiltri();
    filtersRef.current = filtri;
    setHasSearched(true);
    setSelectedPage(1);
    fetchLogs(1, filtri);
  };

  const handleResetFilters = async () => {
    setSelectedUtente(null);
    setSelectedAgenzia(isPartner && partnerAgenziaId
      ? agenzie.find((a) => String(a.id_agenzia) === partnerAgenziaId) ?? null
      : null);
    const resetFilter = {
      id_agenzia: partnerAgenziaId,
      autore: '',
      ruolo_autore: '',
      tipo_evento: '',
      solo_con_voucher: false,
      data_da: '',
      data_a: '',
    };
    setFormDataFilter(resetFilter);
    setLogs([]);
    setTotalPages(1);
    setHasSearched(false);
    setSelectedPage(1);
    filtersRef.current = {};
  };

  const handleAgenziaSelect = (agenzia) => {
    setSelectedAgenzia(agenzia);
    setFormDataFilter((prev) => ({
      ...prev,
      id_agenzia: agenzia ? String(agenzia.id_agenzia) : '',
    }));
    setSelectedUtente(null);
  };

  const agenziaOptions = useMemo(
    () => agenzie.map((a) => ({
      value: a.id_agenzia,
      label: a.denominazione ?? `Agenzia #${a.id_agenzia}`,
      description: [a.indirizzo, a.cap, a.citta, a.provincia].filter(Boolean).join(', '),
      data: a,
    })),
    [agenzie]
  );

  const renderVoucherDetail = (row) => {
    if (!row.ha_voucher || !row.voucher_list?.length) {
      return <span className="text-muted">—</span>;
    }
    return (
      <ul className="mb-0 ps-3 small">
        {row.voucher_list.map((v, idx) => (
          <li key={idx}>
            {v.nome_corso || 'Corso n/d'}
            {v.codice_corso ? ` (${v.codice_corso})` : ''}
            {v.data_utilizzo ? ` — ${UtilityService.formatDate(v.data_utilizzo)}` : ''}
            {v.tipo === 'superpartner' ? ' [SP]' : ''}
          </li>
        ))}
      </ul>
    );
  };

  const isLoading = loadingStateUtenti?.isLoadingLogInviti;
  const idAgenziaFiltro = selectedAgenzia?.id_agenzia ?? (formDataFilter.id_agenzia || null);

  return (
    <div className="container-fluid py-4">
      <h2 className="mb-3">Log inviti e modifiche utenti</h2>
      <p className="text-muted mb-4">
        Traccia creazione, modifica anagrafica ed eliminazione inviti sui corsisti — effettuate da agenzie, admin e altri operatori.
      </p>

      <Form onSubmit={handleFilterSubmit} className="mb-4 p-3 bg-light rounded border">
        <div className="row g-3 align-items-end">
          <div className="col-md-4">
            <Form.Label>Agenzia</Form.Label>
            {isPartner ? (
              <>
                <Form.Control
                  readOnly
                  disabled
                  value={selectedAgenzia?.denominazione ?? '—'}
                />
                <Form.Text className="text-muted">Filtrato sulla tua agenzia.</Form.Text>
              </>
            ) : agenzieLoading ? (
              <div className="text-muted small py-2"><Spinner size="sm" animation="border" className="me-2" />Caricamento agenzie...</div>
            ) : (
              <SearchableSelect
                options={agenziaOptions}
                value={selectedAgenzia?.id_agenzia ?? null}
                onChange={(option) => handleAgenziaSelect(option?.data ?? null)}
                placeholder="Tutte le agenzie"
                emptyLabel="Tutte le agenzie"
              />
            )}
          </div>

          <div className="col-md-5">
            <Form.Label>Utente invitato</Form.Label>
            <SearchUtenteCorsista
              idAgenzia={idAgenziaFiltro}
              selectedUser={selectedUtente}
              onSelect={setSelectedUtente}
            />
          </div>

          <div className="col-md-3">
            <Form.Label>Tipo evento</Form.Label>
            <Form.Select
              value={formDataFilter.tipo_evento}
              onChange={(e) => setFormDataFilter({ ...formDataFilter, tipo_evento: e.target.value })}
            >
              <option value="">Tutti</option>
              <option value="creazione_invito">Creazione invito</option>
              <option value="modifica_anagrafica">Modifica anagrafica</option>
              <option value="eliminazione_invito">Eliminazione invito</option>
            </Form.Select>
          </div>

          <div className="col-md-3">
            <Form.Label>Ruolo autore</Form.Label>
            <Form.Select
              value={formDataFilter.ruolo_autore}
              onChange={(e) => setFormDataFilter({
                ...formDataFilter,
                ruolo_autore: e.target.value,
                autore: e.target.value ? '' : formDataFilter.autore,
              })}
            >
              {AUTORE_RUOLI.map((r) => (
                <option key={r.value || 'all'} value={r.value}>{r.label}</option>
              ))}
            </Form.Select>
          </div>

          <div className="col-md-4">
            <Form.Label>Autore (nome o email)</Form.Label>
            <Form.Control
              placeholder="es. admin@..., Mario Rossi"
              value={formDataFilter.autore}
              disabled={!!formDataFilter.ruolo_autore}
              onChange={(e) => setFormDataFilter({ ...formDataFilter, autore: e.target.value })}
            />
          </div>

          <div className="col-md-2">
            <Form.Label>Dal</Form.Label>
            <Form.Control
              type="date"
              value={formDataFilter.data_da}
              onChange={(e) => setFormDataFilter({ ...formDataFilter, data_da: e.target.value })}
            />
          </div>

          <div className="col-md-2">
            <Form.Label>Al</Form.Label>
            <Form.Control
              type="date"
              value={formDataFilter.data_a}
              onChange={(e) => setFormDataFilter({ ...formDataFilter, data_a: e.target.value })}
            />
          </div>

          <div className="col-md-12">
            <Form.Check
              type="checkbox"
              id="solo-voucher"
              label="Solo eventi con voucher formativo collegato"
              checked={formDataFilter.solo_con_voucher}
              onChange={(e) => setFormDataFilter({ ...formDataFilter, solo_con_voucher: e.target.checked })}
            />
          </div>

          <div className="col-md-12 d-flex gap-2">
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              Cerca
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={handleResetFilters} disabled={isLoading}>
              Azzera filtri
            </button>
          </div>
        </div>
      </Form>

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : !hasSearched ? (
        <div className="text-center text-muted py-5 border rounded bg-white">
          Imposta i filtri e clicca <strong>Cerca</strong> per visualizzare gli eventi.
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Evento</th>
                  <th>Autore</th>
                  <th>Utente invitato</th>
                  <th>Agenzia utente</th>
                  <th>Voucher</th>
                  <th>Dettaglio</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">
                      Nessun evento trovato con i filtri selezionati.
                    </td>
                  </tr>
                ) : (
                  logs.map((row) => (
                    <tr key={row.id_utenti_modifiche_anagrafica_log}>
                      <td className="text-nowrap">
                        {UtilityService.formatDateAndHour(row.rowversion)}
                      </td>
                      <td>
                        <Badge
                          bg={
                            row.tipo_evento === 'eliminazione_invito'
                              ? 'danger'
                              : row.tipo_evento === 'creazione_invito'
                                ? 'success'
                                : 'info'
                          }
                        >
                          {TIPO_EVENTO_LABELS[row.tipo_evento] || row.tipo_evento || 'Modifica'}
                        </Badge>
                      </td>
                      <td>
                        {row.nome_agenzia_modificatore || '—'}
                        {row.tipo_utente_modificatore && !row.nome_agenzia_modificatore?.includes(':') && (
                          <Badge bg="secondary" className="ms-1">
                            {RUOLO_LABELS[row.tipo_utente_modificatore] || row.tipo_utente_modificatore}
                          </Badge>
                        )}
                        {(row.nome_modificatore || row.cognome_modificatore) && (
                          <div className="small">
                            {row.nome_modificatore} {row.cognome_modificatore}
                          </div>
                        )}
                        <div className="small text-muted">{row.username_modificatore}</div>
                      </td>
                      <td>
                        <strong>
                          {row.nome_utente} {row.cognome_utente}
                        </strong>
                        <div className="small text-muted">{row.username_utente}</div>
                      </td>
                      <td>{row.nome_agenzia_utente || '—'}</td>
                      <td>{renderVoucherDetail(row)}</td>
                      <td className="small" style={{ maxWidth: 320 }}>
                        {row.descrizione}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
          {totalPages > 1 && (
            <CustomPagination
              totalPages={totalPages}
              selectedPage={selectedPage}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}
    </div>
  );
};

export default LogInvitiUtentiPage;
